import { DismissalRecord } from './dismissal-record.interface';
import { DismissalSchedule } from './dismissal-schedule.interface';

const DAY_MS = 24 * 60 * 60 * 1000;

export function isDismissalActive(record: DismissalRecord | null, now: number, schedule: DismissalSchedule): boolean {
  if (!record) {
    return false;
  }

  if (record.count >= schedule.maxDismissals) {
    return true;
  }

  const repeatAt = new Date(record.dismissedAt).getTime() + schedule.repeatAfterDays * DAY_MS;

  return Number.isNaN(repeatAt) || now < repeatAt;
}

export function registerDismissal(record: DismissalRecord | null, now: number): DismissalRecord {
  return record ? { ...record, count: record.count + 1 } : { dismissedAt: new Date(now).toISOString(), count: 1 };
}

/**
 * Registers the close of a show that started with `recordAtOpen`. If another tab has already counted a show since then,
 * the stored record is kept, so the same show closed in several tabs (or a stale tab) never adds or rolls back a show.
 */
export function registerShowDismissal(
  recordAtOpen: DismissalRecord | null,
  storedRecord: DismissalRecord | null,
  now: number,
): DismissalRecord {
  return storedRecord && storedRecord.count > (recordAtOpen?.count ?? 0) ? storedRecord : registerDismissal(storedRecord, now);
}

export function parseDismissalRecord(raw: string | null): DismissalRecord | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    return isDismissalRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function readStoredDismissal(storageKey: string): DismissalRecord | null {
  try {
    return parseDismissalRecord(localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

export function storeDismissal(storageKey: string, record: DismissalRecord): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(record));
  } catch {
    // Browser storage is unavailable: the caller keeps its own fallback, if any.
  }
}

// TISS-7648: the ui-state backend is not in the release. Uncomment together with the announcement migration in
// OnlinePaymentAnnouncementDialogService once the backend ships.
// export function removeStoredDismissal(storageKey: string): void {
//   try {
//     localStorage.removeItem(storageKey);
//   } catch {
//     // Browser storage is unavailable: nothing to remove.
//   }
// }

function isDismissalRecord(value: unknown): value is DismissalRecord {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as DismissalRecord).dismissedAt === 'string' &&
    typeof (value as DismissalRecord).count === 'number'
  );
}
